import dayjs from "dayjs";
import clsx from "clsx";
import { db } from "../lib/api";
import { useQuery } from "../lib/hooks";
import { navigate, setQuery, useRoute } from "../lib/router";
import { PageHead } from "../components/Shell";
import { Empty, ErrorBox, Loading, Panel, Seg } from "../components/ui";
import { openPerson } from "../components/PersonDrawer";
import { COUNT_LABEL } from "../lib/refdata";

type Row = {
  user_id: string; name: string; role: string; is_me: boolean; must_do: number; due: number; late_7: number;
  tasks_open: number; tasks_late: number; tasks_due_week: number; tasks_done_week: number; done_today: number;
  logged_today: number; logged_week: number; yesterday: string | null; yesterday_score: number | null; yesterday_on_target: boolean | null;
  streak: number | null; closed_yesterday: boolean; blocked: { id: string; title: string; reason: string | null }[];
  top3: { item_key: string; title: string; why: string; level: string; days_late: number | null; person_id: string | null; link: string | null }[];
};
type Queue = { queue: string; name: string; tasks_open: number; tasks_late: number; holders: number };

type Week = { user_id: string; name: string; role: string; is_me: boolean; counts: Record<string, { actual: number; target: number | null }>;
  goals_total: number; goals_on_track: number; results: { new_contacts: number; appointments_held: number; deals_won: number; conversations: number } };
const KEYS = ["outreach", "follow_ups", "appointments", "client_moves", "recruit_moves", "builder_moves"];

/** This week so far: the six counts against each person's targets, their goals, and what it produced. */
function WeekBoard() {
  const q = useQuery(`teamweek:${dayjs().format("YYYY-MM-DD")}`, () => db.rpc<Week[]>("team_week"), { staleMs: 60_000 });
  if (!q.data?.length) return null;
  return (
    <Panel title="This week so far" hint="Six counts against each person's targets, their goals, and results">
      <div className="table-wrap"><table className="data">
        <thead><tr><th>Person</th>{KEYS.map((k) => <th key={k} className="num">{COUNT_LABEL[k]}</th>)}<th className="num">Goals on track</th>
          <th className="num">New contacts</th><th className="num">Conversations</th><th className="num">Appointments held</th><th className="num">Deals won</th></tr></thead>
        <tbody>{q.data.map((r) => (
          <tr key={r.user_id}>
            <td style={{ minWidth: 150 }}><strong style={{ color: "var(--navy)" }}>{r.name}{r.is_me ? " (you)" : ""}</strong><div className="small muted">{r.role}</div></td>
            {KEYS.map((k) => { const c = r.counts[k] ?? { actual: 0, target: 0 }; const t = c.target ?? 0;
              return <td key={k} className="num"><span className={clsx("cnt", t > 0 && (c.actual >= t ? "ok" : "short"))}>{c.actual}{t > 0 ? ` / ${t}` : ""}</span></td>; })}
            <td className="num">{r.goals_total ? `${r.goals_on_track} / ${r.goals_total}` : "—"}</td>
            <td className="num">{r.results.new_contacts}</td><td className="num">{r.results.conversations}</td>
            <td className="num">{r.results.appointments_held}</td><td className="num">{r.results.deals_won}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </Panel>
  );
}

const pct = (n: number | null) => (n == null ? "—" : `${Math.round(n * 100)}%`);

export default function Scoreboard() {
  const { query } = useRoute();
  const view = (query.get("view") ?? "board") as "board" | "huddle";
  const q = useQuery(`scoreboard:${dayjs().format("YYYY-MM-DD")}`, () => db.rpc<(Row | Queue)[]>("team_scoreboard"), { staleMs: 60_000 });
  const rows = (q.data ?? []).filter((r): r is Row => "user_id" in r);
  const queues = (q.data ?? []).filter((r): r is Queue => "queue" in r);

  return (
    <>
      <PageHead help="tasks" title="Team scoreboard" sub="Each person's week so far and today: results, targets, what's on their list and what they cleared. The huddle view is for a 10-minute stand-up."
        actions={<Seg value={view} onChange={(v) => setQuery({ view: v === "board" ? null : v })} options={[{ value: "board", label: "Scoreboard" }, { value: "huddle", label: "Huddle" }]} />} />
      <ErrorBox error={q.error} />
      {q.loading && !q.data ? <Panel><Loading rows={5} /></Panel> : rows.length === 0 ? <Panel><Empty title="No one yet">Invite your team in Settings → Users & permissions.</Empty></Panel>
      : view === "board" ? (
        <>
          <WeekBoard />
          <Panel title="Today" className="mt16">
            <div className="table-wrap">
              <table className="data">
                <thead><tr>
                  <th>Person</th><th className="num">Must do</th><th className="num">Due today</th><th className="num">7+ days late</th>
                  <th className="num">Tasks open · late</th><th className="num">Tasks this week</th><th className="num">Done today</th>
                  <th className="num">Logged today · week</th><th className="num">Yesterday</th><th className="num">Streak</th><th>Closed the day</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user_id}>
                      <td style={{ minWidth: 170 }}><strong style={{ color: "var(--navy)" }}>{r.name}{r.is_me ? " (you)" : ""}</strong><div className="small muted">{r.role}</div></td>
                      <td className="num"><span className={clsx("pill", r.must_do ? "alert" : "warm")}>{r.must_do}</span></td>
                      <td className="num">{r.due}</td>
                      <td className="num">{r.late_7 ? <span className="pill alert">{r.late_7}</span> : 0}</td>
                      <td className="num">{r.tasks_open} · <span style={{ color: r.tasks_late ? "var(--alert)" : undefined }}>{r.tasks_late}</span></td>
                      <td className="num">{r.tasks_done_week} of {r.tasks_due_week}</td>
                      <td className="num">{r.done_today}</td>
                      <td className="num">{r.logged_today} · {r.logged_week}</td>
                      <td className="num">{r.yesterday ? <span className={clsx("pill", r.yesterday_on_target ? "warm" : r.yesterday_score != null ? "cooling" : "")}>{pct(r.yesterday_score)}</span> : "—"}</td>
                      <td className="num">{r.streak ?? "—"}</td>
                      <td>{r.yesterday ? (r.closed_yesterday ? <span className="pill warm">Yes</span> : <span className="pill alert">No</span>) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          {queues.length > 0 && (
            <Panel title="Queues" hint="Work waiting for whoever holds the role" className="mt16">
              <ul className="cc-list">
                {queues.map((x) => (
                  <li key={x.queue} className={clsx("cc-item", x.tasks_late ? "lv-red" : "lv-amber")}>
                    <button className="cc-main" onClick={() => navigate(`/tasks?scope=all&owner=r:${x.queue}`)}>
                      <span className="cc-title">{x.name}</span>
                      <span className="cc-why">{x.tasks_open} open, {x.tasks_late} late · {x.holders ? `${x.holders} person${x.holders > 1 ? "s" : ""} in the role` : "nobody holds this role yet: it comes to you"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
          <p className="small muted" style={{ marginTop: 10 }}>Must do and due counts are each person's own Command Center list. "Tasks this week" counts tasks due this week that are done.</p>
        </>
      ) : (
        <div className="huddle">
          {rows.map((r) => (
            <div key={r.user_id} className="panel huddle-card">
              <div className="huddle-h">
                <strong>{r.name}</strong>
                <span className={clsx("pill", r.yesterday_on_target ? "warm" : r.yesterday_score != null ? "cooling" : "")}>Yesterday {pct(r.yesterday_score)}</span>
              </div>
              <div className="small muted">{r.must_do} must-do · {r.due} due today · {r.tasks_open} open tasks{r.streak ? ` · ${r.streak}-day streak` : ""}</div>
              <div className="huddle-sub">Top 3 today</div>
              {r.top3.length === 0 ? <div className="small muted">Nothing due.</div> : (
                <ol className="huddle-top">
                  {r.top3.map((i) => (
                    <li key={i.item_key}>
                      <button className="linkish" onClick={() => (i.link ? navigate(i.link) : i.person_id ? openPerson(i.person_id) : undefined)}>
                        <b>{i.title}</b></button> <span className="small muted">{i.why}</span>
                      {i.level === "red" && <span className="pill alert" style={{ marginLeft: 6 }}>{i.days_late && i.days_late > 0 ? `${i.days_late}d late` : "must do"}</span>}
                    </li>
                  ))}
                </ol>
              )}
              <div className="huddle-sub">Blockers</div>
              {r.blocked.length === 0 ? <div className="small muted">None.</div> : (
                <ul className="huddle-top">{r.blocked.map((b) => <li key={b.id}><b>{b.title}</b>{b.reason && <span className="small muted"> · {b.reason}</span>}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
